import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdRemoveRedEye, MdEdit } from 'react-icons/md';
import { IoMdUnlock } from 'react-icons/io';
import toast from 'react-hot-toast';
import Skeleton from '../../../../components/Skeleton';
import TableTemplate from '../../../../components/table/index';
import CustomModal from '../../../../components/modal/index';
import { getAllSufferer, resetPassword } from '../../../../controllers/SuffererController';

const TableComponent = ({ t }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const navigate = useNavigate();
  
  const columns = useMemo(() => [
    { field: 'name', title: t('common.name.title'), sortable: true },
    { field: 'gender', title: t('common.gender.title'), sortable: true },
    { field: 'birth_date', title: t('common.age.title'), sortable: true },
    { field: 'email', title: t('common.email.title'), sortable: true },
    { field: 'phone_number', title: t('common.phoneNumber.title'), sortable: true },
    { field: 'registration_date', title: t('common.registration.title'), sortable: true },
  ], [t]);
  
  const actions = useMemo(() => [
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-successMessage hover:text-successText focus:text-successText'
        title={t('common.action.detail')}
        onClick={() => navigate(`/sufferer/${row.id}/detail`)}
      >
        <MdRemoveRedEye />
      </button>
    ),
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-secondary hover:text-primary focus:text-primary'
        title={t('common.action.edit')}
        onClick={() => navigate(`/sufferer/${row.id}/edit`)}
      >
        <MdEdit />
      </button>
    ),
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-warningMessage hover:text-warningText focus:text-warningText'
        onClick={() => handleOpenResetModal(row.id)}
      >
        <IoMdUnlock />
      </button>
    ),
  ], [t, navigate]);
  
  const filters = useMemo(() => {
    // Gender
    const genderFilters = [
      { field: 'gender', label: t('common.gender.title'), by: t('common.gender.male'), labelBy: t('common.gender.male'), mode: 'exact' },
      { field: 'gender', label: t('common.gender.title'), by: t('common.gender.female'), labelBy: t('common.gender.female'), mode: 'exact' },
    ];
    
    // Age
    const ageRanges = Array.from({ length: 10 }, (_, i) => `${i * 10}-${i * 10 + 9}`);
    const ageFilters = ageRanges.map(range => ({
      field: 'birth_date',
      label: t('common.age.title'),
      by: range,
      labelBy: range,
      mode: 'between',
    }));
    
    return [...genderFilters, ...ageFilters];
  }, [t]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const result = await getAllSufferer();
        const formattedRows = result.sufferers.map((sufferer) => {
          const formattedGender = sufferer.gender.toLowerCase();
          const birthDate = new Date(sufferer.birth_date);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDifference = today.getMonth() - birthDate.getMonth();
          
          if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          };
          
          return {
            id: sufferer.id,
            name: sufferer.name,
            gender: t(`common.gender.${formattedGender}`),
            birth_date: age,
            nik: sufferer.nik,
            email: sufferer.email,
            phone_number: `+62${sufferer.phone_number}`,
            registration_date: sufferer.registration_date
          };
        });
        
        setRows(formattedRows);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      };
    };
    
    fetchData();
  }, [t]);
  
  const handleOpenResetModal = (id) => {
    setSelectedID(id);
    setResetOpen(true);
  };
  
  const handleCloseResetModal = () => {
    setResetOpen(false);
  };
  
  const handleResetPassword = async () => {
    try {
      await resetPassword(selectedID);
      setResetOpen(false);
      toast.success(t('sufferer.resetPassword.message'));
    } catch (error) {
      setResetOpen(false);
      toast.error(error.message);
    };
  };
  
  if (loading) {
    return (
      <Skeleton className='h-96 w-full rounded-lg' />
    );
  };
  
  return (
    <div>
      <TableTemplate
        rows={rows}
        columns={columns}
        actions={actions}
        filters={filters}
        fileName={t('menu.sufferer')}
      />
      
      <CustomModal
        open={resetOpen}
        onClose={handleCloseResetModal}
        onConfirm={handleResetPassword}
        title={t('sufferer.resetPassword.title')}
        description={t('sufferer.resetPassword.description')}
        confirmText={t('common.action.reset')}
        cancelText={t('common.action.cancel')}
      />
    </div>
  );
};

export default TableComponent;
